import * as Sentry from "@sentry/react";
import { jwtDecode } from "jwt-decode";
import { createContext, useContext, useMemo } from "react";
import type { Ability } from "~auth/Ability.ts";
import { useAccessToken } from "~auth/useAccessToken.ts";
import { useAuthInteractionPending } from "~auth/useAuthInteractionPending.ts";
import { Broken } from "~components/shared/ErrorPages/Broken.tsx";
import { Spinner } from "~components/shared/Loading/Spinner/Spinner.tsx";
import { useBusinessConfig } from "~contexts/BusinessConfigContext/BusinessConfigContext.tsx";

interface DecodedToken {
  name: string;
  groups: string[];
  sub: string;
  iss: string;
  preferred_username?: string;
}

export interface UserProfile {
  name: string;
  abilities: Ability[];
  groupIds: string[];
  iss: string;
  sub: string;
}

const UserProfileContext = createContext<UserProfile>({
  name: "anonymous",
  abilities: [],
  groupIds: [],
  iss: "",
  sub: "",
});

export function UserProfileProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const { data: accessToken, isPending } = useAccessToken();
  const isAuthInteractionPending = useAuthInteractionPending();
  const businessConfig = useBusinessConfig();

  const data = useMemo(() => {
    if (!accessToken) {
      return undefined;
    }
    const decodedToken = jwtDecode<DecodedToken>(accessToken);
    const { abilities } = businessConfig;

    Sentry.setUser({
      username: decodedToken.preferred_username,
      email: decodedToken.preferred_username,
    });
    Sentry.setContext("Current User", {
      name: decodedToken.name,
      email: decodedToken.preferred_username,
    });

    return {
      ...decodedToken,
      groupIds: decodedToken.groups,
      abilities: decodedToken.groups
        .map((id) => abilities.find((a) => a.id === id))
        .filter(Boolean)
        .map((a) => a?.displayName)
        .filter((a) => a !== undefined) as Ability[],
    };
  }, [accessToken, businessConfig]);

  // A failed token refresh keeps the last good profile so the running app
  // survives flaky reconnects; Broken only appears when we never had a token.
  if (data) {
    return (
      <StaticUserProfileProvider value={data}>
        {children}
      </StaticUserProfileProvider>
    );
  }

  if (isPending || isAuthInteractionPending) {
    return <Spinner />;
  }

  return <Broken />;
}

export function StaticUserProfileProvider({
  value,
  children,
}: {
  value: UserProfile;
  children: React.ReactNode;
}) {
  return (
    <UserProfileContext.Provider value={value}>
      {children}
    </UserProfileContext.Provider>
  );
}

export function useUserProfile() {
  return useContext(UserProfileContext);
}
